import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import type { PreviousEvaluationResult } from "@services/evaluationService";
import styles from "./EvaluationCard.module.css";

import approveIcon from "@assets/icons/approve-tick.svg";
import overrideIcon from "@assets/icons/edit-icon.svg";
import { ATTEMPT_STATUS, EVALUATION_RESULT_LABELS, type EvaluationResult } from "@constants/evaluationConstants";

interface L1EvaluationCardProps {
  result: PreviousEvaluationResult;
  maxMarks: number;
  onAccept: () => void;
  onOverride: () => void;
  isAccepted: boolean;
}

const ApproveIcon = () => (
  <img src={approveIcon} alt="Approve" width={17} height={17} />
);

const OverrideIcon = () => (
  <img src={overrideIcon} alt="Override" width={16} height={16} />
);

const EvaluationResultBadge = ({ evaluation_result }: { evaluation_result: EvaluationResult }) => {
  const getEvaluationResultStyle = (evaluation_result: EvaluationResult) => {
    switch (evaluation_result) {
      case "correct":
        return { backgroundColor: "#10B981", color: "#ffffff" };
      case "incorrect":
        return { backgroundColor: "#EF4444", color: "#ffffff" };
      case "partially_correct":
        return { backgroundColor: "#F59E0B", color: "#ffffff" };
      default:
        return { backgroundColor: "#aaa3f7", color: "#ffffff" };
    }
  };

  return (
    <span 
      className={styles.evaluationResultBadge}
      style={getEvaluationResultStyle(evaluation_result)}
    >
      {EVALUATION_RESULT_LABELS[evaluation_result]}
    </span>
  );
};

export default function L1EvaluationCard({
  result,
  maxMarks,
  onAccept,
  onOverride,
  isAccepted,
}: L1EvaluationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const feedbackText = result.feedbacks.join(" ");
  const shouldShowReadMore = feedbackText.length > 60;

  const UnattemptedBadge = () => {
    return (
      <span 
        className={styles.badgeGreyed}
      >
        {ATTEMPT_STATUS.UNATTEMPTED}
      </span>
    );
  }

  return (
    <div className={styles.l1Card}>
      {!isAccepted && (
        <span className={styles.scrutinyText}>
          This evaluation requires scrutiny.
        </span>
      )}

      <div className={styles.cardHeader}>
        <div className={styles.labelRow}>
          {!isAccepted && <div className={styles.yellowDot} />}
          <span className={styles.cardTitle}>L1 Evaluation</span>
        </div>
        <div className={styles.badgeHolder}>
        <AnimatePresence>
          {isAccepted && (
            <motion.div
              className={styles.acceptedBadge}
              initial={{
                scale: 1.5,
                opacity: 0,
                rotate: -5,
              }}
              animate={{
                scale: 1,
                opacity: 1,
                rotate: 0,
              }}
              exit={{
                scale: 0.8,
                opacity: 0,
              }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
                mass: 0.5,
              }}
            >
              <ApproveIcon />
              <span className={styles.acceptedBadgeText}>SME Accepted</span>
            </motion.div>
          )}
        </AnimatePresence>
        {result?.attempt_status!==ATTEMPT_STATUS.UNATTEMPTED ? <EvaluationResultBadge evaluation_result={result.evaluation_result} /> : <UnattemptedBadge />}
        </div>
      </div>

      <span className={styles.score}>
        {result.awarded_marks} / {maxMarks}
      </span>

      <div className={styles.feedbackContainer}>
        <span
          className={
            isExpanded || !shouldShowReadMore
              ? styles.feedbackText
              : styles.feedbackTextTruncated
          }
        >
          {feedbackText}
        </span>
        {shouldShowReadMore && (
          <button
            className={styles.readMore}
            onClick={() => setIsExpanded(!isExpanded)}
            type="button"
          >
            {isExpanded ? "read less" : "read more"}
          </button>
        )}
      </div>

      <div className={styles.buttonRow}>
        <button
          className={styles.acceptButton}
          onClick={onAccept}
          disabled={isAccepted}
          type="button"
        >
          <span className={styles.acceptButtonText}>Accept</span>
        </button>
        <button
          className={styles.overrideButton}
          onClick={onOverride}
          type="button"
        >
          <OverrideIcon />
          <span className={styles.overrideButtonText}>Override</span>
        </button>
      </div>
    </div>
  );
}
